import React from 'react'
import { Alert, Button, Flex, Form, Input, Modal, Radio } from 'antd'
import { useDispatch } from 'react-redux'
import { openNotificationWithIcon } from '../../../../../../../utils/notification.helper'
import { toggleCreateTextAssignmentModal } from '../../../../../../../store/slices/assignment.slice'
import {
    useCreateAssignmentFromZipMutation,
    useCreateTextAssignmentFromCsvMutation,
    useCreateTextAssignmentFromCsvOldMutation,
    useCreateTextAssignmentFromZipMutation,
} from '../../../../../../../store/apis/assignment.api'
import { ASSIGNMENT_TYPES } from '../../../../../../../constants/assignment-types'
import { InboxOutlined } from '@ant-design/icons'
import Dragger from 'antd/es/upload/Dragger'
import { isCsv } from '../../../../../../../utils/file.helper'
import { TextSubmissionTypes, getTextSubmissionTypeDescription, getTextSubmissionTypeName } from '../../../../../../../constants/text-submission-types'
import { useSelector } from 'react-redux'
import authPersistSelectors from '../../../../../../../store/selectors/auth.persist.selectors'
import { useAutoFocusFirstInput } from '../../../../../../../../hooks/useAutoFocusFirstInput'

const CreateTextAssignmentForm = () => {
    const dispatch = useDispatch()
    const developerMode = useSelector(authPersistSelectors.isDeveloperMode)

    const [createTextAssignmentFromCsv, createTextAssignmentFromCsvResult] = useCreateTextAssignmentFromCsvMutation()
    const [createTextAssignmentFromCsvOld, createTextAssignmentFromCsvOldResult] =
        useCreateTextAssignmentFromCsvOldMutation()
    const [createTextAssignmentFromZip, createTextAssignmentFromZipResult] = useCreateTextAssignmentFromZipMutation()
    const [createOldTextAssignmentFromZip, createOldTextAssignmentFromZipResult] = useCreateAssignmentFromZipMutation()
    const [form] = Form.useForm()
    useAutoFocusFirstInput()

    const handleCreate = async values => {
        const { file, method, name, submissionType } = values
        const isNewMethod = method !== 'Old'
        const hardcodedType = ASSIGNMENT_TYPES.MODEL.value
        
        // Handle creation without a file (empty assignment)
        if (isNewMethod && submissionType === TextSubmissionTypes.DOCUMENT) {
            try {
                const response = await createTextAssignmentFromCsv({ 
                    name, 
                    type: hardcodedType,
                    file: null,
                    submission_type: submissionType 
                })
                
                if ('data' in response) {
                    dispatch(toggleCreateTextAssignmentModal())
                    openNotificationWithIcon('success', 'Assignment created successfully')
                }
                return
            } catch (error) {
                console.error('Error creating empty assignment:', error)
                return
            }
        }

        // If there's a file, determine format and use appropriate mutation
        const isCsvFile = file && isCsv(file.type)

        if (isNewMethod) {
            // If submission type wasn't explicitly set (e.g. if user selected "None" which is null), determine from file type
            const effectiveSubmissionType = submissionType || (isCsvFile 
                ? TextSubmissionTypes.QUIZ 
                : TextSubmissionTypes.DOCUMENT)
            
            const mutation = effectiveSubmissionType === TextSubmissionTypes.QUIZ ? createTextAssignmentFromCsv : createTextAssignmentFromZip
            const response = await mutation({ 
                name, 
                type: hardcodedType,
                file: file || null,
                submission_type: effectiveSubmissionType 
            })

            if (response?.data?.rejectedFileNames?.length) {
                Modal.success({
                    title: 'Success',
                    content: `Assignment was created successfully, but the following submissions were rejected: ${response.data.rejectedFileNames.join(', ')}.`,
                })
            } else if ('data' in response) {
                dispatch(toggleCreateTextAssignmentModal())
                openNotificationWithIcon('success', 'Assignment created successfully')
            }
        } else {
            // Legacy method (old)
            const mutation = isCsvFile ? createTextAssignmentFromCsvOld : createOldTextAssignmentFromZip
            const response = await mutation({ name, type: hardcodedType, file })

            if (response?.data?.rejectedFileNames?.length) {
                Modal.success({
                    title: 'Success',
                    content: `Assignment was created successfully, but the following submissions were rejected: ${response.data.rejectedFileNames.join(', ')}.`,
                })
            } else if ('data' in response) {
                dispatch(toggleCreateTextAssignmentModal())
                openNotificationWithIcon('success', 'Assignment created successfully')
            }
        }
    }

    const submissionTypeOptions = Object.values(TextSubmissionTypes)
        .map(type => ({
            label: getTextSubmissionTypeName(type),
            value: type,
        }))

    const method = Form.useWatch('method', form) || 'New'
    const currentSubmissionType = Form.useWatch('submissionType', form)

    const draggerProps = {
        name: 'file',
        accept: '.csv',
        multiple: false,
        listType: 'picture',
        maxCount: 1,
        beforeUpload: () => false,
    }


    const isError =
        createTextAssignmentFromCsvResult.isError ||
        createTextAssignmentFromCsvOldResult.isError ||
        createTextAssignmentFromZipResult.isError ||
        createOldTextAssignmentFromZipResult.isError

    const errorMessage =
        createTextAssignmentFromCsvResult?.error ||
        createTextAssignmentFromCsvOldResult?.error ||
        createTextAssignmentFromZipResult?.error ||
        createOldTextAssignmentFromZipResult?.error

    return (
        <Form
            name='create-text-assignment'
            onFinish={handleCreate}
            form={form}
            autoComplete='off'
            layout='vertical'
            requiredMark={false}
            validateTrigger={['onBlur', 'onSubmit']}
        >
            <Form.Item
                name='name'
                rules={[{ required: true, message: 'Please, enter name for the new assignment' }]}
                label='Name'
            >
                <Input />
            </Form.Item>
            <Flex justify='space-between'>
                {method === 'New' && (
                    <>
                        <Form.Item 
                            name='submissionType' 
                            label='Type'
                            rules={[{ required: true, message: 'Please, select a type'}]}
                            initialValue={TextSubmissionTypes.QUIZ}
                        >
                            <Radio.Group optionType='button'>
                                {submissionTypeOptions.map(option => (
                                    <Radio.Button key={option.value} value={option.value} title={getTextSubmissionTypeDescription(option.value)}>
                                        {option.label}
                                    </Radio.Button>
                                ))}
                            </Radio.Group>
                        </Form.Item>
                    </>
                )}
                {developerMode && ( // Conditionally render method selector
                    <Form.Item name='method' label='Method' initialValue='New'>
                        <Radio.Group options={['New', 'Old']} optionType='button' />
                    </Form.Item>
                )}
            </Flex>
            {currentSubmissionType !== TextSubmissionTypes.DOCUMENT && (
                <Form.Item
                    name='file'
                    label='Submissions'
                    getValueFromEvent={event => event.file}
                    rules={[
                        {
                            message: 'Please, select CSV file',
                            validator: (_, currentFileValue) => {
                                if (method === 'New') {
                                    return Promise.resolve()
                                }
                                if (currentFileValue) {
                                    return Promise.resolve()
                                }
                                return Promise.reject(new Error('Please, select CSV file'))
                            },
                        },
                    ]}
                >
                        <Dragger {...draggerProps} aria-label='CSV file'>
                            <p className='ant-upload-drag-icon'>
                                <InboxOutlined />
                            </p>
                            <p className='ant-upload-text'>Click or drag CSV file to this area</p>
                            <p className='ant-upload-hint'>or leave empty and add submissions later</p>
                        </Dragger>
                </Form.Item>
            )}

            
            {isError && <Alert message={errorMessage} type='error' showIcon closable />}
            <Form.Item>
                <Button
                    type='primary'
                    htmlType='submit'
                    block
                    loading={
                        createTextAssignmentFromCsvResult.isLoading || 
                        createTextAssignmentFromZipResult.isLoading ||
                        createTextAssignmentFromCsvOldResult.isLoading ||
                        createOldTextAssignmentFromZipResult.isLoading
                    }
                >
                    Create
                </Button>
            </Form.Item>
        </Form>
    )
}

export default CreateTextAssignmentForm
